import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { Button, Col, message, Row } from 'antd';
import { yupResolver } from '@hookform/resolvers/yup';
import { Container } from '@/components/Elements';
import { YourRefinanceDetailFormType } from '../types';

import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import StepperFormLayout from '@/components/Layout/StepperFormLayout';
import { refinanceFormRoutes } from '../constant/refinanceStepperRoutes';
import useScrollToTop from '@/hooks/useScrollTop';
import { useFetchUserProfileQuery } from '@/store/apis/userApi';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import {
  usePostRefinanceRequestMutation,
  useGetRefinanceStepDetailQuery,
} from '@/pages/mortgage/apis/refinanceAPI';
import NewPersonalDetail from './NewPersonalDetail';
import NewPropertyDetail from './NewPropertyDetail';
import { primaryDetailSchema } from '../schema/primaryDetailSchema';
import StepProgress from '@/components/StepProgress/StepProgress';
import { stepData } from '../constant/stepData';
import makeAllFieldsOptional from '@/utils/yupUtils';
import { ObjectSchema } from 'yup';
import { useAppSelector } from '@/hooks/hooks';

const PrimaryDetail = () => {
  const { id } = useParams();
  useUnsavedChangesWarning(true);
  useScrollToTop();
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const total = 5;
  const [postRefinanceRequest, { isLoading: postRefinanceRequestLoading }] =
    usePostRefinanceRequestMutation();

  const [postRefinanceDraft, { isLoading: postRefinanceDraftLoading }] =
    usePostRefinanceRequestMutation();

  const brokerId = useAppSelector((state) => state.broker.brokerId);

  const {
    data: userProfileData,
    // error: userProfileDataError,
    // isLoading: userProfileDataLoading,
  } = useFetchUserProfileQuery(undefined, {
    skip: !!id,
  });

  const {
    data: personalDetailApiResponse,
    // error: fetchPersonalDetailError,
    // isLoading: fetchPersonalDetailLoading,
  } = useGetRefinanceStepDetailQuery(
    { application_id: id!, step: 'primary_detail' },
    { skip: !id, refetchOnMountOrArgChange: true },
  );
  const yourRefinanceDetailData = personalDetailApiResponse?.data.data as any;

  const primaryDetailSchemaWithOptionalFields =
    makeAllFieldsOptional(primaryDetailSchema);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    unregister,
    resetField,
    formState: { errors },
  } = useForm<YourRefinanceDetailFormType>({
    defaultValues: {
      refinancePriority: [],
    },
    resolver: yupResolver(
      primaryDetailSchemaWithOptionalFields as ObjectSchema<any>,
    ),
  });

  const saveAsDraft = async () => {
    const rawData = getValues();
    const initialData = primaryDetailSchemaWithOptionalFields.cast(rawData, {
      stripUnknown: true,
    });
    const data = Object.fromEntries(
      Object.entries(initialData).filter(
        ([_, value]) =>
          value != null &&
          value !== undefined &&
          value !== '' &&
          (!Array.isArray(value) || value.length > 0),
      ),
    );
    const payload = {
      data,
      draft: true,
      step: 'primary_detail' as const,
      ...(id ? { application_id: id } : {}),
      ...(brokerId ? { broker_id: brokerId } : {}),
    };
    try {
      const res = await postRefinanceDraft(payload as any);
      message.success(res?.data?.message);
    } catch (error) {
      message.error('Error while saving as draft');
    }
  };

  const onSubmit = async (data: YourRefinanceDetailFormType) => {
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(
        ([_, value]) =>
          value !== null &&
          value !== undefined &&
          value !== '' &&
          (!Array.isArray(value) || value.length > 0),
      ),
    );

    try {
      const res = await postRefinanceRequest({
        data: filteredData as YourRefinanceDetailFormType,
        step: 'primary_detail',
        application_id: id,
        ...(brokerId ? { broker_id: brokerId } : {}),
      });
      const applicationId = res?.data?.data?.application_id;

      if (applicationId) {
        displaySuccess('Successfully submitted details.');
        navigate(`${refinanceFormRoutes['income_detail']}/${applicationId}`);
      } else {
        throw new Error('Application ID not found in response');
      }
    } catch (err) {
      displayError(err);
    }
  };

  const handleFormSubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  const fieldOrder = [
    'citizenship',
    'citizenshipDetails',
    'currentResidentalStatus',
    'currentResidentalStatusDetails',
    'maritalStatus',
    'maritalStatusDetails',
  ];

  return (
    <StepperFormLayout
      handleSubmit={handleFormSubmit}
      showBackButton={false}
      nextBtnText="Next: Income Details"
      isSubmitLoading={postRefinanceRequestLoading}
      showDraftButton={true}
      handleSaveAsDraftClick={saveAsDraft}
      isDraftLoading={postRefinanceDraftLoading}
    >
      <Container width="md">
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Row
            style={{
              paddingBottom: '20px',
            }}
            gutter={[0, 4]}
          >
            <Col xs={24}>
              <StepProgress
                formId="personal_detail"
                id={id || ''}
                completedStep={0}
                totalSteps={total}
                stepData={stepData}
              />
            </Col>
          </Row>
          <Row>
            <NewPersonalDetail
              data={yourRefinanceDetailData}
              setValue={setValue}
              watch={watch}
              control={control}
              errors={errors}
              unregister={unregister}
              resetField={resetField}
              getValues={getValues}
            />
            <NewPropertyDetail
              data={yourRefinanceDetailData}
              setValue={setValue}
              watch={watch}
              control={control}
              errors={errors}
              unregister={unregister}
            />
            <Col xs={24}>
              <Col xs={24}>
                <Button
                  ref={submitButtonRef}
                  type="primary"
                  htmlType="submit"
                  style={{ display: 'none' }}
                >
                  Submit
                </Button>
              </Col>
            </Col>
          </Row>
        </form>
      </Container>
    </StepperFormLayout>
  );
};

export default PrimaryDetail;
